import { TicketPriority, TicketStatus } from "@swivl-mono/types";
import { useEffect, useState } from "react";

import { Member } from "../../../Models/Member/Member.Current";
import { useTicketingStore } from "../../../Models/Ticketing/Ticketing.Store";

const storageKey = (): string => `ticketing:inbox:filters:${Member.id ?? "unknown"}`;

interface PersistedFilters {
  sortBy?: "createdAt" | "updatedAt" | "status" | "priority";
  sortDir?: "asc" | "desc";
  statuses?: TicketStatus[];
  priority?: TicketPriority;
  overdue?: boolean;
  createdByMe?: boolean;
}

function loadFiltersFromStorage(): PersistedFilters {
  try {
    const stored = localStorage.getItem(storageKey());
    if (stored) {
      const parsed = JSON.parse(stored) as PersistedFilters;
      // The API only accepts createdAt/updatedAt sorts. Drop any other value
      // (e.g. a legacy "priority"/"status" sort persisted by an older build) —
      // sending it would 422 every list request.
      if (parsed.sortBy && parsed.sortBy !== "createdAt" && parsed.sortBy !== "updatedAt") {
        delete parsed.sortBy;
        delete parsed.sortDir;
      }
      return parsed;
    }
  } catch {
    // ignore parse errors
  }
  return {};
}

function saveFiltersToStorage(filters: PersistedFilters): void {
  try {
    localStorage.setItem(storageKey(), JSON.stringify(filters));
  } catch {
    // ignore storage errors
  }
}

/**
 * Persists inbox filters (sort/status/priority/overdue/created-by-me) across
 * reloads.
 *
 * Location scoping is deliberately NOT applied here anymore: since ENG-2882
 * the server enforces the member's location scope on every ticket query
 * (including the assignment/claim override that keeps a member's own tickets
 * visible when their location moves out of scope). A client-side `locationIds`
 * filter would narrow browse views past the server predicate and hide those
 * assigned tickets, so it was removed with the backend enforcement.
 */
export function useInboxFilterPersistence(): void {
  const query = useTicketingStore((s) => s.inboxQuery);
  const setQuery = useTicketingStore((s) => s.setInboxQuery);
  const refresh = useTicketingStore((s) => s.refreshInbox);
  const [initialized, setInitialized] = useState(false);

  // Load persisted filters on mount.
  useEffect(() => {
    if (initialized) return;

    const persisted = loadFiltersFromStorage();

    if (Object.keys(persisted).length > 0) {
      setQuery({
        sortBy: persisted.sortBy,
        sortDir: persisted.sortDir,
        statuses: persisted.statuses,
        priority: persisted.priority,
        overdue: persisted.overdue,
        createdByMemberId: persisted.createdByMe ? Member.id : undefined,
        offset: 0
      });
      void refresh();
    }

    setInitialized(true);
  }, [initialized, setQuery, refresh]);

  // Persist filters whenever they change.
  useEffect(() => {
    if (!initialized) return;
    saveFiltersToStorage({
      sortBy: query.sortBy,
      sortDir: query.sortDir,
      statuses: query.statuses,
      priority: query.priority,
      overdue: query.overdue,
      createdByMe: query.createdByMemberId === Member.id
    });
  }, [initialized, query.sortBy, query.sortDir, query.statuses, query.priority, query.overdue, query.createdByMemberId]);
}
